(() => {
  'use strict';
  const homeSelector = '[data-fsb-home]';
  const barSelector = '.gta-content.GSC-BAR-kWHCocIrolnI';
  const headerSelector = 'sticky-header, .header-wrapper';
  const headerStateSelector = '.section-header, sticky-header, .header-wrapper';
  const measuredSelector = `${barSelector}, ${headerSelector}`;
  const scrolledClass = 'fsb-home-scrolled';
  const marginProperty = '--fsb-scroll-margin';
  const roots = new Set();
  const previousMargins = new WeakMap();
  const observedElements = new Set();
  const observedHeaderStates = new Set();
  let frame = null;
  let active = false;
  let compact = false;

  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
  const mutationObserver = typeof MutationObserver === 'function' ? new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node =>
      node.nodeType === 1 && (node.matches(measuredSelector) || node.querySelector(measuredSelector))
    ))) schedule();
  }) : null;
  const headerStateObserver = typeof MutationObserver === 'function' ? new MutationObserver(schedule) : null;

  function schedule() {
    if (!active || frame !== null) return;
    frame = requestAnimationFrame(update);
  }

  function visibleRect(node) {
    if (!node.getClientRects().length) return null;
    const style = getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) return null;
    const rect = node.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight ? rect : null;
  }

  function onHeaderTransition(event) {
    if (event.target.matches?.(headerStateSelector)) schedule();
  }

  function update() {
    frame = null;
    if (!active) return;
    const y = Math.max(0, window.scrollY || 0);
    compact = compact ? y > 56 : y > 96;
    document.body.classList.toggle(scrolledClass, compact);
    const bars = [...document.querySelectorAll(barSelector)];
    const headers = [...document.querySelectorAll(headerSelector)];
    const elements = [...bars, ...headers];
    observedElements.forEach(node => {
      if (!elements.includes(node)) {
        resizeObserver?.unobserve(node);
        observedElements.delete(node);
      }
    });
    elements.forEach(node => {
      if (!observedElements.has(node)) {
        resizeObserver?.observe(node);
        observedElements.add(node);
      }
    });
    const headerStates = new Set(headers.flatMap(header => [header, header.closest('.section-header')].filter(Boolean)));
    if (headerStates.size !== observedHeaderStates.size || [...headerStates].some(node => !observedHeaderStates.has(node))) {
      headerStateObserver?.disconnect();
      observedHeaderStates.clear();
      headerStates.forEach(node => {
        headerStateObserver?.observe(node, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
        observedHeaderStates.add(node);
      });
    }
    let bottom = 0;
    bars.forEach(bar => {
      const rect = visibleRect(bar);
      if (rect) bottom = Math.max(bottom, Math.min(rect.bottom, window.innerHeight));
    });
    const barBottom = bottom;
    headers.forEach(header => {
      const rect = visibleRect(header);
      if (!rect) return;
      let owner = header.closest('.section-header') || header;
      let style = getComputedStyle(owner);
      if (style.position !== 'sticky' && style.position !== 'fixed') {
        owner = header;
        style = getComputedStyle(owner);
      }
      if (style.position !== 'sticky' && style.position !== 'fixed') return;
      const inset = parseFloat(style.top);
      if (style.position === 'sticky' && !Number.isFinite(inset)) return;
      const ownerRect = owner === header ? rect : visibleRect(owner);
      if (!ownerRect || ownerRect.top > Math.max(barBottom, Number.isFinite(inset) ? inset : 0) + 1) return;
      bottom = Math.max(bottom, Math.min(rect.bottom, window.innerHeight));
    });
    const margin = `${Math.ceil(bottom) + 16}px`;
    roots.forEach(root => {
      if (root.style.getPropertyValue(marginProperty) !== margin) root.style.setProperty(marginProperty, margin);
    });
  }

  function mount(scope = document) {
    const homes = [...scope.querySelectorAll(homeSelector)];
    if (scope.matches?.(homeSelector)) homes.unshift(scope);
    homes.forEach(root => {
      if (roots.has(root)) return;
      previousMargins.set(root, { value: root.style.getPropertyValue(marginProperty), priority: root.style.getPropertyPriority(marginProperty) });
      roots.add(root);
    });
    if (!roots.size) return;
    if (!active) {
      active = true;
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      window.addEventListener('pageshow', schedule);
      document.addEventListener('transitionend', onHeaderTransition);
      window.visualViewport?.addEventListener('resize', schedule, { passive: true });
      mutationObserver?.observe(document.body, { childList: true, subtree: true });
    }
    schedule();
  }

  function unmount(scope) {
    roots.forEach(root => {
      if (root !== scope && !scope.contains(root)) return;
      const previous = previousMargins.get(root);
      if (previous?.value) root.style.setProperty(marginProperty, previous.value, previous.priority);
      else root.style.removeProperty(marginProperty);
      previousMargins.delete(root);
      roots.delete(root);
    });
    if (roots.size || !active) return;
    active = false;
    compact = false;
    if (frame !== null) cancelAnimationFrame(frame);
    frame = null;
    window.removeEventListener('scroll', schedule);
    window.removeEventListener('resize', schedule);
    window.removeEventListener('pageshow', schedule);
    document.removeEventListener('transitionend', onHeaderTransition);
    window.visualViewport?.removeEventListener('resize', schedule);
    resizeObserver?.disconnect();
    mutationObserver?.disconnect();
    headerStateObserver?.disconnect();
    observedElements.clear();
    observedHeaderStates.clear();
    document.body.classList.remove(scrolledClass);
  }

  mount();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => unmount(event.target));
})();
